import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type EventListItem, type MyStatus } from '../lib/api';
import {
  DISCIPLINES,
  MON,
  placeKept,
  TYPES,
  dShort,
  holdsFullPlace,
  plural,
  timeLabel,
  undoStatus,
  wd,
  ymd,
} from '../lib/domain';
import { eventTooltip, firstLine, stripMd } from '../lib/md';
import { Avatar, CountSelect, DiscChips, DiscIcon, ErrorBox, Icon, Loading, PageHead, ToggleChip } from '../ui';
import { useToast } from '../hooks';
import { dc } from '../lib/style';

const PAGE = 200;
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

const CHOICES: ['yes' | 'maybe' | 'none', string][] = [
  ['yes', 'Dabei'],
  ['maybe', 'Vielleicht'],
  ['none', 'Nicht dabei'],
];
const DONE: Record<'yes' | 'maybe' | 'none', string> = { yes: 'Zugesagt', maybe: 'Vielleicht', none: 'Abgesagt' };

/** "Dabei" in the list: a tap signs up / off as before; the arrow next to it offers Vielleicht and Nicht dabei */
export function RsvpButton({ e }: { e: EventListItem }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const arrow = useRef<HTMLButtonElement>(null);
  const close = (focusArrow: boolean) => {
    setOpen(false);
    if (focusArrow) arrow.current?.focus();
  };
  const m = useMutation({
    mutationFn: (v: { status: 'yes' | 'maybe' | 'none'; undo?: boolean }) =>
      api(`/events/${e.id}/rsvp`, { method: 'PUT', body: { status: v.status } }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['event', e.id] });
      if (v.undo) return toast('Rückgängig gemacht');
      // "Rückgängig" restores the status from before the tap (the server keeps the old list position)
      const before = undoStatus(e.my_status);
      // a full event: the place is kept a few minutes – say so (with "Rückgängig")
      const kept = v.status !== 'yes' && holdsFullPlace(e);
      const canUndo = !(e.capacity && before === 'maybe');
      toast(
        kept ? placeKept(v.status) : `${DONE[v.status]}: ${e.title}`,
        canUndo ? () => m.mutate({ status: before, undo: true }) : undefined,
      );
    },
    onError: err => toast(err.message),
  });
  // the menu closes on a tap outside or Escape
  useEffect(() => {
    if (!open) return;
    const away = (ev: Event) => {
      if (!box.current?.contains(ev.target as Node)) setOpen(false);
    };
    const esc = (ev: KeyboardEvent) => ev.key === 'Escape' && close(true);
    // on open: the current choice has the focus (keyboard and screen readers start in the list)
    box.current?.querySelector<HTMLButtonElement>('.rsvp-menu [aria-pressed="true"]')?.focus();
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  if (e.cancelled) return <span className="st st-cancel">Abgesagt</span>;
  const s: MyStatus = e.my_status;
  const label = { none: 'Zusagen', yes: 'Dabei', wait: 'Warteliste', maybe: 'Vielleicht' }[s];
  const current = s === 'wait' ? 'yes' : s;
  return (
    // the tile itself opens the event: taps in here stay here
    <span
      className="rsvp-split"
      ref={box}
      onClick={ev => ev.stopPropagation()}
      // leaving the list with Tab closes it
      onBlur={ev => {
        // (a tap outside is handled by pointerdown; Safari does not focus tapped buttons: no relatedTarget then)
        if (open && ev.relatedTarget && !box.current?.contains(ev.relatedTarget)) setOpen(false);
      }}
    >
      <button
        className="rsvp-btn"
        data-s={s}
        disabled={m.isPending}
        aria-label={`${label}: ${e.title}`}
        onClick={() => m.mutate({ status: s === 'none' ? 'yes' : 'none' })}
      >
        {s === 'yes' && <Icon name="check" sw={2.4} />}
        {label}
      </button>
      <button
        ref={arrow}
        className="rsvp-more"
        data-s={s}
        aria-label={`Teilnahme wählen: ${e.title}`}
        aria-expanded={open}
        aria-controls={`rsvp-${e.id}`}
        onClick={() => setOpen(!open)}
      >
        <Icon name="chevron" sw={2.2} />
      </button>
      {open && (
        <span className="rsvp-menu" id={`rsvp-${e.id}`} role="group" aria-label="Teilnahme">
          {CHOICES.filter(([v]) => v !== 'maybe' || !e.capacity).map(([v, l]) => (
            <button
              key={v}
              type="button"
              aria-pressed={current === v}
              onClick={() => {
                close(true); // the focus goes back to the arrow (it stays enabled, so it can take it)
                if (v !== current && !m.isPending) m.mutate({ status: v });
              }}
            >
              {current === v ? <Icon name="check" sw={2.4} /> : <span style={{ width: 15 }} />}
              {l}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function Card({ e }: { e: EventListItem }) {
  const nav = useNavigate();
  const past = e.past;
  const confirmed = e.capacity ? Math.min(e.yes, e.capacity) : e.yes;
  const wait = e.capacity ? Math.max(0, e.yes - e.capacity) : 0;
  const snip = firstLine(e.description).slice(0, 160);
  // phones have no hover: the full text and who is coming fold open in the tile
  const [open, setOpen] = useState(false);
  const full = stripMd(e.description)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const foldable = full.length > snip.length + 5 || e.preview.length > 0;
  return (
    <div
      className={`card${past ? ' is-past' : ''}${e.cancelled ? ' is-cancel' : ''}`}
      title={eventTooltip(e, `${dShort(new Date(e.starts_at))} · ${timeLabel(e)}`)}
      role="button"
      tabIndex={0}
      style={dc(e.discipline)}
      onClick={() => void nav(`/termine/${e.id}`)}
      onKeyDown={ev => {
        // Enter on the RSVP button inside must not also open the event
        if (ev.key === 'Enter' && ev.target === ev.currentTarget) void nav(`/termine/${e.id}`);
      }}
    >
      <DiscIcon disc={e.discipline} title={DISCIPLINES[e.discipline]} />
      <div className="c-body">
        <div className="c-meta">
          <span className="c-time">{timeLabel(e)}</span>
          <span className="tag">
            {DISCIPLINES[e.discipline]} · {TYPES[e.type]}
          </span>
          {e.series_id && (
            <span className="badge">
              <Icon name="repeat" />
              Serie
            </span>
          )}
        </div>
        <div className="c-title">{e.title}</div>
        {open ? (
          <div className="c-full" id={`c-full-${e.id}`}>
            {full && <p>{full.length > 1200 ? `${full.slice(0, 1200).trimEnd()} …` : full}</p>}
            {e.preview.length > 0 && (
              <p className="c-names">
                <b>Dabei:</b> {e.preview.map(p => p.name).join(', ')}
                {confirmed > e.preview.length ? ` und ${confirmed - e.preview.length} weitere` : ''}
              </p>
            )}
          </div>
        ) : (
          snip && <div className="c-snip">{snip}</div>
        )}
        <div className="c-foot">
          {e.location && (
            <span className="ic">
              <Icon name="pin" />
              <span>{e.location}</span>
            </span>
          )}
          <span className="ic">
            <span className="avs">
              {e.preview.map((p, i) => (
                <Avatar key={i} person={p} />
              ))}
            </span>
            <span>
              {confirmed} dabei{e.maybe ? ` · ${e.maybe} vielleicht` : ''}
              {wait ? ` · ${wait} Warteliste` : ''}
            </span>
          </span>
          {e.capacity && (
            <span className="ic">
              <span className={`meter${confirmed >= e.capacity ? ' full' : ''}`}>
                <i style={{ width: `${Math.min(100, (confirmed / e.capacity) * 100)}%` }} />
              </span>{' '}
              {confirmed}/{e.capacity}
            </span>
          )}
          {e.comments > 0 && (
            <span className="ic">
              <Icon name="chat" />
              {e.comments}
            </span>
          )}
          {foldable && (
            <button
              className="c-fold"
              aria-expanded={open}
              aria-controls={`c-full-${e.id}`}
              aria-label={open ? `Weniger anzeigen: ${e.title}` : `Mehr anzeigen: ${e.title}`}
              onClick={ev => {
                ev.stopPropagation();
                setOpen(!open);
              }}
              onKeyDown={ev => ev.stopPropagation()}
            >
              <span aria-hidden="true">{open ? '▴' : '▾'}</span>
              {open ? 'Weniger' : 'Mehr'}
            </button>
          )}
        </div>
      </div>
      {!past && <RsvpButton e={e} />}
    </div>
  );
}

export default function Termine() {
  const [scope, setScope] = useState<'upcoming' | 'archive'>('upcoming');
  const [q, setQ] = useState('');
  const [discs, setDiscs] = useState<Set<string>>(new Set());
  const [mine, setMine] = useState(false);
  // Pages of PAGE events; the archive (years of history) loads older pages on demand
  const {
    data: pages,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['events', scope],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<EventListItem[]>(`/events?scope=${scope}&limit=${PAGE}&offset=${pageParam}`),
    getNextPageParam: (last, all) => (last.length < PAGE ? undefined : all.length * PAGE),
    placeholderData: keepPreviousData, // switching Kommend/Archiv keeps the list (and the dropdown) until the new one is there
  });
  const data = useMemo(() => pages?.pages.flat(), [pages]);

  const list = useMemo(() => {
    const toks = norm(q).split(/\s+/).filter(Boolean);
    return (data || []).filter(
      e =>
        (!discs.size || discs.has(e.discipline)) &&
        (!mine || e.my_status !== 'none') &&
        (!toks.length ||
          toks.every(t =>
            norm(
              `${e.title} ${stripMd(e.description)} ${e.location} ${DISCIPLINES[e.discipline]} ${TYPES[e.type]} ${e.organizer?.name || ''}`,
            ).includes(t),
          )),
    );
  }, [data, q, discs, mine]);

  const groups = useMemo(() => {
    const out: { key: string; label: string; days: { key: string; date: Date; events: EventListItem[] }[] }[] = [];
    for (const e of list) {
      const d = new Date(e.starts_at),
        mk = `${d.getFullYear()}-${d.getMonth()}`,
        dk = ymd(d);
      let g = out[out.length - 1];
      if (!g || g.key !== mk) {
        g = { key: mk, label: `${MON[d.getMonth()]} ${d.getFullYear()}`, days: [] };
        out.push(g);
      }
      let day = g.days[g.days.length - 1];
      if (!day || day.key !== dk) {
        day = { key: dk, date: d, events: [] };
        g.days.push(day);
      }
      day.events.push(e);
    }
    return out;
  }, [list]);
  const today = ymd(new Date());

  return (
    <>
      <PageHead
        title="Termine"
        right={
          <Link className="btn btn-primary" to="/termine/neu">
            <Icon name="plus" sw={2.2} />
            Termin anlegen
          </Link>
        }
      />
      <div className="toolbar" role="search">
        <label className="field">
          <Icon name="search" sw={1.8} />
          <input
            type="search"
            placeholder="Suchen: Titel, Ort, Beschreibung …"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Termine durchsuchen"
          />
        </label>
        {/* the sports on their own row */}
        <DiscChips value={discs} onChange={setDiscs} />
      </div>
      {/* the count line stays usable while loading or after an error (e.g. back from Archiv to Kommend) */}
      <div className="resbar">
        {data && <span>{plural(list.length, 'Termin', 'Termine')}</span>}
        <CountSelect
          label="Zeitraum"
          value={scope}
          onChange={setScope}
          options={[
            ['upcoming', 'Kommend'],
            ['archive', 'Archiv'],
          ]}
        />
        <ToggleChip pressed={mine} onChange={setMine} icon="user">
          Nur meine
        </ToggleChip>
      </div>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : (
        <>
          {!list.length && (
            <div className="empty">
              <b>Keine Termine gefunden.</b>
              <p>Probiere einen anderen Suchbegriff oder entferne Filter.</p>
            </div>
          )}
          {groups.map(g => (
            <section key={g.key}>
              <h2 className="month-h">
                {g.label}{' '}
                <small>
                  {plural(
                    g.days.reduce((n, d) => n + d.events.length, 0),
                    'Termin',
                    'Termine',
                  )}
                </small>
              </h2>
              {g.days.map(d => (
                <div key={d.key} className={`day${d.key === today ? ' is-today' : ''}`}>
                  <div className="day-date">
                    <b>{d.date.getDate()}</b>
                    <span>{wd(d.date)}</span>
                    {d.key === today && <em>Heute</em>}
                  </div>
                  <div className="day-events">
                    {d.events.map(e => (
                      <Card key={e.id} e={e} />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          {hasNextPage && (
            <div className="more">
              <button className="btn" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                {isFetchingNextPage ? 'Lädt …' : scope === 'archive' ? 'Ältere Termine laden' : 'Weitere Termine laden'}
              </button>
              {(q || discs.size > 0 || mine) && <p>Suche und Filter beziehen sich auf die bisher geladenen Termine.</p>}
            </div>
          )}
        </>
      )}
    </>
  );
}
